/**
 * Pairing generation.
 *
 * This is a social golfer problem: partition N players into foursomes across R
 * rounds so that everybody plays with and against everybody as evenly as
 * possible. It is deliberately NOT random — a random draw reliably produces
 * two guys who never see each other all week and two who are stuck together
 * four times.
 *
 * The problem decomposes cleanly, and that decomposition is the whole trick:
 *
 *   outer (hard)    — which four players share a group. Search.
 *   inner (trivial) — given a foursome, there are exactly THREE ways to split
 *                     it into two pairs (ab|cd, ac|bd, ad|bc). Enumerate all
 *                     three and pick the best. No search, ever.
 *
 * That inner structure is exactly what the 2025 sheet does by hand: Match 1
 * plays Tyler-Danny and Thomas-Phil; Match 2 keeps the same foursome and plays
 * Tyler-Thomas and Danny-Phil. Same four men, a different one of the three
 * matchings.
 */

import { hash32, mulberry32 } from './rank'
import type { PlayerId } from './types'

export type RoundType = 'oneVsOne' | 'twoVsTwo' | 'freeForAll'

export interface RoundSpec {
  id: string
  type: RoundType
  /**
   * Reuse another round's foursomes instead of searching fresh — the Match 1 /
   * Match 2 relationship.
   */
  reuseGroupsFrom?: string
  /** Force a different internal matching from that round. */
  mustDifferFrom?: string
  /**
   * Balance group handicaps. Matters where you are ranked only against your own
   * groupmates (Match 4), where an easy group is worth real money.
   */
  balanceHandicaps?: boolean
  /** Groups the admin has locked; the optimizer may not touch these players. */
  pinnedGroups?: PlayerId[][]
}

export interface PairingPlayer {
  id: PlayerId
  courseHandicap: number
}

export interface PlannedGroup {
  playerIds: PlayerId[]
  /** Index into MATCHINGS, or null for a round with no internal pairing. */
  matchingIndex: number | null
}

export interface PlannedRound {
  roundId: string
  groups: PlannedGroup[]
}

export interface PairingPlan {
  rounds: PlannedRound[]
}

export interface CostWeights {
  together: number
  opponent: number
  partner: number
  cap: number
  consecutive: number
  handicapBalance: number
  teeSlot: number
}

export const DEFAULT_WEIGHTS: CostWeights = {
  together: 10,
  opponent: 8,
  partner: 6,
  cap: 40,
  consecutive: 5,
  handicapBalance: 3,
  teeSlot: 2,
}

/** The three ways to split four players into two pairs. */
export const MATCHINGS: [number, number][][] = [
  [
    [0, 1],
    [2, 3],
  ],
  [
    [0, 2],
    [1, 3],
  ],
  [
    [0, 3],
    [1, 2],
  ],
]

// ---------------------------------------------------------------------------
// matrices
// ---------------------------------------------------------------------------

export interface Matrices {
  together: number[][]
  opponent: number[][]
  partner: number[][]
}

function zeros(n: number): number[][] {
  return Array.from({ length: n }, () => new Array<number>(n).fill(0))
}

export function buildMatrices(
  plan: PairingPlan,
  specs: RoundSpec[],
  index: Map<PlayerId, number>,
): Matrices {
  const n = index.size
  const m: Matrices = { together: zeros(n), opponent: zeros(n), partner: zeros(n) }
  const bump = (grid: number[][], a: PlayerId, b: PlayerId) => {
    const i = index.get(a)!
    const j = index.get(b)!
    grid[i]![j]! += 1
    grid[j]![i]! += 1
  }

  for (const round of plan.rounds) {
    const spec = specs.find((s) => s.id === round.roundId)!
    for (const g of round.groups) {
      const ids = g.playerIds
      for (let i = 0; i < ids.length; i++)
        for (let j = i + 1; j < ids.length; j++) bump(m.together, ids[i]!, ids[j]!)

      if (spec.type === 'freeForAll') {
        // Everyone in the group is ranked against everyone else.
        for (let i = 0; i < ids.length; i++)
          for (let j = i + 1; j < ids.length; j++) bump(m.opponent, ids[i]!, ids[j]!)
        continue
      }
      if (g.matchingIndex == null) continue

      const pairs = MATCHINGS[g.matchingIndex]!
      if (spec.type === 'oneVsOne') {
        for (const [x, y] of pairs) bump(m.opponent, ids[x]!, ids[y]!)
      } else {
        // 2v2: the two pairs are the partnerships, and every cross pair is an
        // opponent.
        const sideA = [ids[pairs[0]![0]]!, ids[pairs[0]![1]]!]
        const sideB = [ids[pairs[1]![0]]!, ids[pairs[1]![1]]!]
        bump(m.partner, sideA[0]!, sideA[1]!)
        bump(m.partner, sideB[0]!, sideB[1]!)
        for (const a of sideA) for (const b of sideB) bump(m.opponent, a, b)
      }
    }
  }
  return m
}

// ---------------------------------------------------------------------------
// cost
// ---------------------------------------------------------------------------

export interface Targets {
  together: number
  opponent: number
  partner: number
  togetherCap: number
}

export function computeTargets(
  specs: RoundSpec[],
  n: number,
  groupSize = 4,
): Targets {
  const R = specs.length
  const opponentsPerPlayer = specs.reduce(
    (s, r) =>
      s + (r.type === 'oneVsOne' ? 1 : r.type === 'twoVsTwo' ? 2 : groupSize - 1),
    0,
  )
  const partnerRounds = specs.filter((r) => r.type === 'twoVsTwo').length
  return {
    together: (R * (groupSize - 1)) / (n - 1),
    opponent: opponentsPerPlayer / (n - 1),
    partner: partnerRounds / (n - 1),
    togetherCap: Math.ceil((R * (groupSize - 1)) / (n - 1)) + 1,
  }
}

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0)

export function cost(
  plan: PairingPlan,
  specs: RoundSpec[],
  players: PairingPlayer[],
  w: CostWeights,
  targets: Targets,
): number {
  const index = new Map(players.map((p, i) => [p.id, i]))
  const m = buildMatrices(plan, specs, index)
  const n = players.length
  let c = 0

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      c += w.together * (m.together[i]![j]! - targets.together) ** 2
      c += w.opponent * (m.opponent[i]![j]! - targets.opponent) ** 2
      c += w.partner * (m.partner[i]![j]! - targets.partner) ** 2
      // Exceeding the ceiling is much worse than falling short of the target:
      // "we played together four times" is the complaint people actually make.
      c += w.cap * Math.max(0, m.together[i]![j]! - targets.togetherCap) ** 2
    }
  }

  // Back-to-back repeats read as a lazy draw even when the weekly totals are fair.
  for (let r = 1; r < plan.rounds.length; r++) {
    const prev = plan.rounds[r - 1]!
    const cur = plan.rounds[r]!
    const spec = specs.find((s) => s.id === cur.roundId)
    if (spec?.reuseGroupsFrom) continue // deliberate reuse is not a repeat
    for (const g of cur.groups) {
      for (const pg of prev.groups) {
        const shared = g.playerIds.filter((p) => pg.playerIds.includes(p)).length
        if (shared >= 2) c += w.consecutive * (shared - 1)
      }
    }
  }

  const chOf = new Map(players.map((p) => [p.id, p.courseHandicap]))
  const fieldMean = mean(players.map((p) => p.courseHandicap))
  for (const round of plan.rounds) {
    const spec = specs.find((s) => s.id === round.roundId)
    if (!spec?.balanceHandicaps) continue
    for (const g of round.groups) {
      const chs = g.playerIds.map((id) => chOf.get(id) ?? 0)
      c += w.handicapBalance * (mean(chs) - fieldMean) ** 2
    }
  }

  // Nobody should go off last every single day.
  for (const p of players) {
    const slots: number[] = []
    for (const round of plan.rounds) {
      const i = round.groups.findIndex((g) => g.playerIds.includes(p.id))
      if (i >= 0) slots.push(i)
    }
    const mu = mean(slots)
    c += w.teeSlot * mean(slots.map((s) => (s - mu) ** 2))
  }

  return c
}

// ---------------------------------------------------------------------------
// search
// ---------------------------------------------------------------------------

export interface OptimizeOptions {
  seed: string
  restarts: number
  iterations: number
  t0: number
  alpha: number
  weights: CostWeights
  groupSize: number
}

export const DEFAULT_OPTIONS: Omit<OptimizeOptions, 'seed'> = {
  restarts: 6,
  iterations: 12000,
  t0: 1,
  alpha: 0.9995,
  weights: DEFAULT_WEIGHTS,
  groupSize: 4,
}

function chunk<T>(xs: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size))
  return out
}

function shuffled<T>(xs: T[], rng: () => number): T[] {
  const a = [...xs]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[a[i], a[j]] = [a[j]!, a[i]!]
  }
  return a
}

function seedPlan(
  specs: RoundSpec[],
  players: PairingPlayer[],
  groupSize: number,
  rng: () => number,
): PairingPlan {
  const rounds: PlannedRound[] = []
  for (const spec of specs) {
    if (spec.reuseGroupsFrom) {
      const src = rounds.find((r) => r.roundId === spec.reuseGroupsFrom)
      rounds.push({
        roundId: spec.id,
        groups: (src?.groups ?? []).map((g) => ({ ...g, matchingIndex: null })),
      })
      continue
    }
    const pinned = spec.pinnedGroups ?? []
    const locked = new Set(pinned.flat())
    const free = shuffled(
      players.map((p) => p.id).filter((id) => !locked.has(id)),
      rng,
    )
    const groups: PlannedGroup[] = [
      ...pinned.map((ids) => ({ playerIds: [...ids], matchingIndex: null })),
      ...chunk(free, groupSize).map((ids) => ({ playerIds: ids, matchingIndex: null })),
    ]
    rounds.push({ roundId: spec.id, groups })
  }
  return { rounds }
}

function clone(plan: PairingPlan): PairingPlan {
  return {
    rounds: plan.rounds.map((r) => ({
      roundId: r.roundId,
      groups: r.groups.map((g) => ({
        playerIds: [...g.playerIds],
        matchingIndex: g.matchingIndex,
      })),
    })),
  }
}

/**
 * Propagate a reused round's groups after a move, so a round that mirrors
 * another never drifts out of sync with it.
 */
function syncReused(plan: PairingPlan, specs: RoundSpec[]): void {
  for (const spec of specs) {
    if (!spec.reuseGroupsFrom) continue
    const src = plan.rounds.find((r) => r.roundId === spec.reuseGroupsFrom)
    const dst = plan.rounds.find((r) => r.roundId === spec.id)
    if (!src || !dst) continue
    dst.groups = src.groups.map((g, i) => ({
      playerIds: [...g.playerIds],
      matchingIndex: dst.groups[i]?.matchingIndex ?? null,
    }))
  }
}

/** Swap two players between groups, or rotate three, never touching a pin. */
function propose(
  plan: PairingPlan,
  specs: RoundSpec[],
  rng: () => number,
): PairingPlan | null {
  const movable = plan.rounds.filter((r) => {
    const spec = specs.find((s) => s.id === r.roundId)!
    return !spec.reuseGroupsFrom && r.groups.length > 1
  })
  if (!movable.length) return null

  const next = clone(plan)
  const round = movable[Math.floor(rng() * movable.length)]!
  const target = next.rounds.find((r) => r.roundId === round.roundId)!
  const spec = specs.find((s) => s.id === target.roundId)!
  const locked = new Set((spec.pinnedGroups ?? []).flat())

  const pick = () => {
    for (let tries = 0; tries < 20; tries++) {
      const gi = Math.floor(rng() * target.groups.length)
      const g = target.groups[gi]!
      const pi = Math.floor(rng() * g.playerIds.length)
      if (!locked.has(g.playerIds[pi]!)) return { gi, pi }
    }
    return null
  }

  const a = pick()
  const b = pick()
  if (!a || !b || a.gi === b.gi) return null

  const ga = target.groups[a.gi]!
  const gb = target.groups[b.gi]!

  // A plain two-swap gets stuck in local minima that only a three-way rotation
  // can leave, because improving one pair's exposure often requires worsening
  // a second before a third improves.
  if (rng() < 0.3) {
    const c = pick()
    if (c && c.gi !== a.gi && c.gi !== b.gi) {
      const gc = target.groups[c.gi]!
      const tmp = ga.playerIds[a.pi]!
      ga.playerIds[a.pi] = gc.playerIds[c.pi]!
      gc.playerIds[c.pi] = gb.playerIds[b.pi]!
      gb.playerIds[b.pi] = tmp
      syncReused(next, specs)
      return next
    }
  }

  ;[ga.playerIds[a.pi], gb.playerIds[b.pi]] = [gb.playerIds[b.pi]!, ga.playerIds[a.pi]!]

  syncReused(next, specs)
  return next
}

/**
 * Choose the internal matching for every group.
 *
 * Exact, not searched: three candidates per foursome, so we evaluate all of
 * them. A round marked `mustDifferFrom` has that round's choice removed from
 * the candidate set, which is how Match 2 is guaranteed to re-pair the same
 * foursome differently.
 */
function assignMatchings(
  plan: PairingPlan,
  specs: RoundSpec[],
  players: PairingPlayer[],
  w: CostWeights,
  targets: Targets,
): void {
  for (const round of plan.rounds) {
    const spec = specs.find((s) => s.id === round.roundId)!
    if (spec.type === 'freeForAll') {
      for (const g of round.groups) g.matchingIndex = null
      continue
    }
    const from = spec.mustDifferFrom
      ? plan.rounds.find((r) => r.roundId === spec.mustDifferFrom)
      : undefined

    round.groups.forEach((g, gi) => {
      if (g.playerIds.length !== 4) {
        g.matchingIndex = 0
        return
      }
      const forbidden = from?.groups[gi]?.matchingIndex ?? -1
      let best = 0
      let bestCost = Infinity
      for (let idx = 0; idx < MATCHINGS.length; idx++) {
        if (idx === forbidden) continue
        g.matchingIndex = idx
        const c = cost(plan, specs, players, w, targets)
        if (c < bestCost) {
          bestCost = c
          best = idx
        }
      }
      g.matchingIndex = best
    })
  }
}

/**
 * Build a draw.
 *
 * Deterministic: the same seed and reshuffle counter always produce the same
 * plan, so a draw can be reproduced and audited. Bumping the counter gives a
 * different-but-equally-fair draw.
 */
export function optimize(
  specs: RoundSpec[],
  players: PairingPlayer[],
  options: Partial<OptimizeOptions> & { seed: string },
): { plan: PairingPlan; cost: number } {
  const opts: OptimizeOptions = { ...DEFAULT_OPTIONS, ...options }
  const targets = computeTargets(specs, players.length, opts.groupSize)
  let best: PairingPlan | null = null
  let bestCost = Infinity

  for (let restart = 0; restart < opts.restarts; restart++) {
    const rng = mulberry32(hash32(`${opts.seed}#${restart}`))
    let cur = seedPlan(specs, players, opts.groupSize, rng)
    assignMatchings(cur, specs, players, opts.weights, targets)
    let curCost = cost(cur, specs, players, opts.weights, targets)
    let temp = opts.t0

    for (let it = 0; it < opts.iterations; it++) {
      const cand = propose(cur, specs, rng)
      if (cand) {
        const candCost = cost(cand, specs, players, opts.weights, targets)
        const delta = candCost - curCost
        if (delta < 0 || rng() < Math.exp(-delta / temp)) {
          cur = cand
          curCost = candCost
        }
      }
      temp *= opts.alpha
    }

    assignMatchings(cur, specs, players, opts.weights, targets)
    curCost = cost(cur, specs, players, opts.weights, targets)
    if (curCost < bestCost) {
      bestCost = curCost
      best = cur
    }
  }

  return { plan: best!, cost: bestCost }
}

// ---------------------------------------------------------------------------
// reporting
// ---------------------------------------------------------------------------

export interface FairnessReport {
  targets: Targets
  /** Highest number of times any pair shared a group. */
  maxTogether: number
  minTogether: number
  /** Pairs who never shared a group all week. */
  neverTogether: [PlayerId, PlayerId][]
  /** Pairs who shared a group more than the ceiling. */
  overExposed: [PlayerId, PlayerId, number][]
  matrices: Matrices
}

/** What the admin sees before accepting a draw, and after hand-editing one. */
export function fairnessReport(
  plan: PairingPlan,
  specs: RoundSpec[],
  players: PairingPlayer[],
): FairnessReport {
  const index = new Map(players.map((p, i) => [p.id, i]))
  const matrices = buildMatrices(plan, specs, index)
  const targets = computeTargets(specs, players.length)
  const never: [PlayerId, PlayerId][] = []
  const over: [PlayerId, PlayerId, number][] = []
  let max = 0
  let min = Infinity

  for (let i = 0; i < players.length; i++) {
    for (let j = i + 1; j < players.length; j++) {
      const v = matrices.together[i]![j]!
      max = Math.max(max, v)
      min = Math.min(min, v)
      if (v === 0) never.push([players[i]!.id, players[j]!.id])
      if (v > targets.togetherCap) over.push([players[i]!.id, players[j]!.id, v])
    }
  }

  return {
    targets,
    maxTogether: max,
    minTogether: min === Infinity ? 0 : min,
    neverTogether: never,
    overExposed: over,
    matrices,
  }
}

/**
 * A matching reads two different ways depending on the round type, so it gets
 * two accessors rather than one ambiguous name.
 *
 * In a singles round `[[0,1],[2,3]]` means two CONTESTS — player 0 against
 * player 1, player 2 against player 3. In a team round the same data means two
 * SIDES — 0 and 1 partner against 2 and 3.
 */
export function contestsFor(group: PlannedGroup): [PlayerId, PlayerId][] | null {
  if (group.matchingIndex == null || group.playerIds.length !== 4) return null
  return MATCHINGS[group.matchingIndex]!.map(([x, y]) => [
    group.playerIds[x]!,
    group.playerIds[y]!,
  ]) as [PlayerId, PlayerId][]
}

export function sidesFor(group: PlannedGroup): [PlayerId[], PlayerId[]] | null {
  if (group.matchingIndex == null || group.playerIds.length !== 4) return null
  const [p, q] = MATCHINGS[group.matchingIndex]!
  return [
    [group.playerIds[p![0]]!, group.playerIds[p![1]]!],
    [group.playerIds[q![0]]!, group.playerIds[q![1]]!],
  ]
}
